import type { AnalyticsEvent, PostAnalyticsFN } from '../common/Analytics';
import type { ErrorReporter } from '../common/ErrorReporter';
import type { Entry } from '../db/DB';
import { ReplicantError } from '../Errors';
import type { Replicant, ReplicantConfig } from '../ReplicantConfig';
import type { WithMeta } from '../systemStateFields';
import type { ExecutionContext, GetReadOnlyEntryFn } from './ServerReplicant';

export class ServerReplicantAnalytics<T extends Replicant> {
    constructor(
        private opts: {
            config: ReplicantConfig<T>;
            dateNow: () => number;
            errorReporter: ErrorReporter;
            getReadOnlyEntry: GetReadOnlyEntryFn<T>;
            postAnalytics: PostAnalyticsFN;
        },
    ) {}

    async sendAnalyticsEvents(events: AnalyticsEvent[], context?: ExecutionContext): Promise<void> {
        try {
            const eventsWithExtraProps = await this.withExtraEventAndUserProperties(events, context);
            await this.opts.postAnalytics(eventsWithExtraProps);
        } catch (e: any) {
            await this.opts.errorReporter.captureException(e);
        }
    }

    /** @returns `events` supplemented with extra event and user properties derived from receiver state. */
    private async withExtraEventAndUserProperties(
        events: AnalyticsEvent[],
        context?: ExecutionContext,
    ): Promise<AnalyticsEvent[]> {
        const timestamp = this.opts.dateNow();

        try {
            const extraPropsPerReceiver = await this.getExtraPropsPerReceiver(events, context);

            return events.map((event) => {
                if (!('user_id' in event)) {
                    return event;
                }

                const { lastLoginAt, userProperties } = extraPropsPerReceiver[event.user_id] ?? {};

                // Match GCInstant's default event properties:
                const defaultEventProperties: { [key: string]: unknown } = {
                    timestamp: new Date(timestamp).toISOString(),
                    version: this.opts.config.version,
                };

                if (lastLoginAt) {
                    defaultEventProperties.sessionElapsedTime = timestamp - lastLoginAt;
                }

                return {
                    ...event,
                    event_properties: { ...defaultEventProperties, ...event.event_properties },
                    user_properties: { ...userProperties, ...event.user_properties },
                } as AnalyticsEvent;
            });
        } catch (e: any) {
            await this.opts.errorReporter.captureException(
                new ReplicantError(
                    'Resolving analytics extra properties failed: ' + e.message,
                    'server_error',
                    'analytics_extra_properties_error',
                    undefined,
                    { events, cause: e },
                ),
            );

            return events;
        }
    }

    private async getExtraPropsPerReceiver(
        events: AnalyticsEvent[],
        context?: ExecutionContext,
    ): Promise<{ [receiverId: string]: { lastLoginAt?: number; userProperties: { [key: string]: unknown } } }> {
        const uniqueReceiverIds = Array.from(
            new Set(events.map((e) => ('user_id' in e ? e.user_id : '')).filter((id) => !!id)),
        );

        const entries: { [receiverId: string]: Entry<any> } = {};

        await Promise.all(
            uniqueReceiverIds.map(async (receiverId) => {
                const { entryPromises } = context ?? { entryPromises: {} };

                if (!entryPromises[receiverId]) {
                    entryPromises[receiverId] = this.opts
                        .getReadOnlyEntry(receiverId, { skipWrites: true })
                        .then((result) => (result?.entry as Entry<WithMeta<any, any, any>> | undefined) ?? null);
                }

                const entry = await entryPromises[receiverId];

                if (entry) {
                    entries[receiverId] = entry;
                }
            }),
        );

        const userPropsPerReceiver: {
            [receiverId: string]: { lastLoginAt?: number; userProperties: { [key: string]: unknown } };
        } = {};

        const now = this.opts.dateNow();
        const api = { date: { now: () => now } };

        for (const receiverId of uniqueReceiverIds) {
            const receiverEntry = entries[receiverId];

            if (receiverEntry) {
                userPropsPerReceiver[receiverId] = {
                    lastLoginAt: receiverEntry.lastLoginAt,
                    userProperties: this.opts.config.getReceiverUserProperties?.(receiverEntry.state, api) ?? {},
                };
            }
        }

        return userPropsPerReceiver;
    }
}
